import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { User } from '@/types';
import { ROLE_PRESETS, PermissionKey } from '@/types/permission';

interface AuthState {
  user: User | null;
  token: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;

  // Actions
  setUser: (user: User | null) => void;
  setToken: (token: string | null) => void;
  setLoading: (loading: boolean) => void;
  logout: () => void;

  // Permission helpers
  hasPermission: (permission: PermissionKey) => boolean;
  hasAnyPermission: (permissions: PermissionKey[]) => boolean;
  hasAllPermissions: (permissions: PermissionKey[]) => boolean;
  isSuperAdmin: () => boolean;
  isTenantAdmin: () => boolean;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: true,

      setUser: (user) =>
        set({
          user,
          isAuthenticated: !!user,
        }),

      setToken: (token) => set({ token }),

      setLoading: (isLoading) => set({ isLoading }),

      logout: () =>
        set({
          user: null,
          token: null,
          isAuthenticated: false,
        }),

      hasPermission: (permission) => {
        const { user } = get();
        if (!user) return false;

        // Super admin has all permissions
        if (user.role === 'super_admin') return true;

        // Check role preset permissions
        const rolePermissions = ROLE_PRESETS[user.role as keyof typeof ROLE_PRESETS] || [];
        if ((rolePermissions as readonly string[]).includes(permission)) return true;

        // Check custom user permissions
        return user.permissions?.[permission] === true;
      },

      hasAnyPermission: (permissions) => {
        const { hasPermission } = get();
        return permissions.some((p) => hasPermission(p));
      },

      hasAllPermissions: (permissions) => {
        const { hasPermission } = get();
        return permissions.every((p) => hasPermission(p));
      },

      isSuperAdmin: () => {
        const { user } = get();
        return user?.role === 'super_admin';
      },

      isTenantAdmin: () => {
        const { user } = get();
        return user?.role === 'tenant_admin' || user?.role === 'super_admin';
      },
    }),
    {
      name: 'roni-panel-auth', // Separate storage key from chat app
      partialize: (state) => ({
        user: state.user,
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
